import React, { useRef, useState, useEffect } from 'react';
import './Slider.css';
const steps = [0, 25, 50, 75, 100];
const clamp = value => Math.max(0, Math.min(100, Number(value) || 0));
export default function SliderQuantity({ value, onSliderChange, direction, stateUpdate }) {
  const track = useRef(null), gesture = useRef(null);
  const [draft, setDraft] = useState(null);
  const frame = useRef(null), pending = useRef(0);
  const callbacks = useRef({ onSliderChange, stateUpdate });
  callbacks.current = { onSliderChange, stateUpdate };
  const cancelFrame = () => { if (frame.current !== null) cancelAnimationFrame(frame.current); frame.current = null; };
  useEffect(() => () => cancelFrame(), []);
  const updateLive = next => {
    setDraft(next);
    pending.current = next;
    if (frame.current !== null) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      callbacks.current.onSliderChange?.(pending.current);
      callbacks.current.stateUpdate?.(false);
    });
  };
  const rawValue = Number(value) || 0;
  const overLimit = draft === null && rawValue > 100;
  const shown = draft === null ? clamp(rawValue) : draft;
  const displayPercent = overLimit ? rawValue : shown;
  const commit = next => { cancelFrame(); onSliderChange?.(next); stateUpdate?.(false); setDraft(null); };
  const position = event => {
    const rect = track.current.getBoundingClientRect();
    const raw = clamp((event.clientX - rect.left - (gesture.current?.offset || 0)) / rect.width * 100);
    return raw;
  };
  return <div className={'slider-container order-size-slider ' + (direction === 'Sell' ? 'sell' : 'buy') + (overLimit ? ' slider-over' : '')}>
    <div className="slider-readout" aria-hidden="true">{Number(displayPercent.toFixed(2))}%</div>
    <div className="slider-track" ref={track} role="slider" tabIndex={0} aria-label="Order size percentage" aria-valuemin={0} aria-valuemax={100} aria-valuenow={shown}
      onKeyDown={event => {
        const key = event.key;
        if (!['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(key)) return;
        event.preventDefault();
        commit(key === 'Home' ? 0 : key === 'End' ? 100 : clamp(shown + (key === 'ArrowRight' || key === 'ArrowUp' ? 1 : -1)));
      }}
      onPointerDown={event => {
        if (event.button !== 0 || event.target.closest('button')) return;
        event.preventDefault();
        const rect = track.current.getBoundingClientRect();
        const dot = event.target.closest('[data-step]');
        gesture.current = { startX: event.clientX, moved: false, step: dot ? Number(dot.dataset.step) : null, offset: event.target.closest('.slider-thumb') ? event.clientX - rect.left - shown / 100 * rect.width : 0 };
        stateUpdate?.(false);
        event.currentTarget.setPointerCapture(event.pointerId); updateLive(gesture.current.step ?? position(event));
      }}
      onPointerMove={event => {
        if (!gesture.current) return;
        if (Math.abs(event.clientX - gesture.current.startX) > 3) gesture.current.moved = true;
        if (gesture.current.moved) updateLive(position(event));
      }}
      onPointerUp={event => {
        if (gesture.current === null) return;
        const next = !gesture.current.moved && gesture.current.step !== null ? gesture.current.step : position(event); gesture.current = null;
        event.currentTarget.releasePointerCapture(event.pointerId); commit(next);
      }}
      onPointerCancel={() => { cancelFrame(); gesture.current = null; setDraft(null); }}>
      <div className="slider-fill" style={{ width: shown + '%' }} />
      {steps.map(step => <React.Fragment key={step}>
        <button type="button" className={'slider-percent-label' + (Math.abs(shown - step) < 0.01 && !overLimit ? ' selected' : '')} style={{ left: step + '%' }} onClick={() => commit(step)}>{step}%</button>
        <div data-step={step} className={'slider-step ' + (shown >= step ? 'active' : '')} style={{ left: step + '%' }} />
      </React.Fragment>)}
      <div className="slider-thumb" style={{ left: shown + '%' }} />
    </div>
  </div>;
}
